import { format } from 'date-fns';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  useApproveCaseContextFieldMutation,
  useUnapproveCaseContextFieldMutation,
} from '@/services/api/caseContextApprovalService';
import { ApprovalInfo } from '@/services/types/case-context-types';

interface Props {
  matterId: string;
  fieldName: string;
  approval?: ApprovalInfo | null;
  label?: string;
  compact?: boolean;
}

export const CaseContextApprovalToggle: React.FC<Props> = ({
  matterId,
  fieldName,
  approval,
  label,
  compact = false,
}) => {
  const [approveField, { isLoading: isApproving }] =
    useApproveCaseContextFieldMutation();
  const [unapproveField, { isLoading: isUnapproving }] =
    useUnapproveCaseContextFieldMutation();
  const [optimisticApproved, setOptimisticApproved] = useState<boolean | null>(
    null,
  );

  const isApproved =
    optimisticApproved !== null
      ? optimisticApproved
      : (approval?.approved ?? false);
  const isLoading = isApproving || isUnapproving;

  const handleToggle = async (checked: boolean) => {
    // Set optimistic state
    setOptimisticApproved(checked);

    try {
      if (checked) {
        await approveField({ matterId, fieldName }).unwrap();
        toast.success(`${formatFieldName(fieldName)} approved`);
      } else {
        await unapproveField({ matterId, fieldName }).unwrap();
        toast.success(`${formatFieldName(fieldName)} approval revoked`);
      }
      // Clear optimistic state on success
      setOptimisticApproved(null);
    } catch (error) {
      // Revert optimistic state on error
      setOptimisticApproved(null);
      const action = checked ? 'approve' : 'revoke approval for';
      toast.error(`Failed to ${action} ${formatFieldName(fieldName)}`);
    }
  };

  const formatFieldName = (name: string): string => {
    return name
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const displayLabel = label || formatFieldName(fieldName);

  const switchElement = (
    <Switch
      id={`approval-${fieldName}`}
      checked={isApproved}
      onCheckedChange={handleToggle}
      disabled={isLoading}
      className={cn(
        'data-[state=checked]:bg-green-600 h-6 w-11',
        isLoading && 'opacity-50 cursor-wait',
        // Override the thumb translation
        '[&>span]:data-[state=checked]:translate-x-5',
      )}
    />
  );

  if (compact) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2">{switchElement}</div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1 text-xs">
            <div className="font-medium">{displayLabel}</div>
            {isApproved && approval?.approved_by_name && (
              <>
                <div>Approved by: {approval.approved_by_name}</div>
                {approval.approved_at && (
                  <div>
                    On:{' '}
                    {format(
                      new Date(approval.approved_at),
                      'MMM dd, yyyy h:mm a',
                    )}
                  </div>
                )}
              </>
            )}
            {!isApproved && (
              <div className="text-muted-foreground">Not approved</div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <div className="flex items-center justify-between space-x-2">
      <Label
        htmlFor={`approval-${fieldName}`}
        className="text-sm font-normal cursor-pointer"
      >
        {displayLabel}
      </Label>
      <Tooltip>
        <TooltipTrigger asChild>{switchElement}</TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1 text-xs">
            {isApproved && approval?.approved_by_name && (
              <>
                <div>Approved by: {approval.approved_by_name}</div>
                {approval.approved_at && (
                  <div>
                    On:{' '}
                    {format(
                      new Date(approval.approved_at),
                      'MMM dd, yyyy h:mm a',
                    )}
                  </div>
                )}
              </>
            )}
            {!isApproved && (
              <div className="text-muted-foreground">Click to approve</div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </div>
  );
};
